import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  downloadCharityReceipts,
  downloadDonationReceipt,
  ReceiptDownloadError
} from '../../api/receipt.api';
import type { AddIcNumberPurpose } from '../../components/donation/AddIcNumberSheet';
import { RECEIPT_DOWNLOAD_MESSAGES } from '../../constants/donation.const';
import {
  DONATION_ERROR_CODES,
  type DonorTaxProfile
} from '../../types/donation.types';
import { useDonorTaxProfile } from './useDonations';

/** Which file was asked for: it decides the message when the file is too large. */
export type ReceiptDownloadKind = 'receipt' | 'bundle' | 'summary';

/** The plain message for a download that failed. */
export const receiptDownloadMessage = (
  error: unknown,
  kind: ReceiptDownloadKind = 'receipt'
): string => {
  if (!(error instanceof ReceiptDownloadError))
    return RECEIPT_DOWNLOAD_MESSAGES.failed;
  if (error.status === 429) return RECEIPT_DOWNLOAD_MESSAGES.tooManyDownloads;
  if (error.status === 503) return RECEIPT_DOWNLOAD_MESSAGES.busy;
  switch (error.code) {
    case DONATION_ERROR_CODES.DONOR_NAME_REQUIRED:
      return RECEIPT_DOWNLOAD_MESSAGES.nameRequired;
    case DONATION_ERROR_CODES.DONOR_EMAIL_REQUIRED:
      return RECEIPT_DOWNLOAD_MESSAGES.emailRequired;
    case DONATION_ERROR_CODES.DOCUMENT_TOO_LARGE:
      return kind === 'summary'
        ? RECEIPT_DOWNLOAD_MESSAGES.summaryTooLarge
        : RECEIPT_DOWNLOAD_MESSAGES.bundleTooLarge;
    default:
      return RECEIPT_DOWNLOAD_MESSAGES.failed;
  }
};

/** Runs a PDF download and shows its failure as a plain toast. */
export const downloadWithToast = (
  download: () => Promise<void>,
  kind: ReceiptDownloadKind = 'receipt'
) =>
  download().catch((error: unknown) => {
    toast.error(receiptDownloadMessage(error, kind));
  });

interface PendingDownload {
  download: () => Promise<void>;
  kind: ReceiptDownloadKind;
}

const isIdRequired = (error: unknown) =>
  error instanceof ReceiptDownloadError &&
  error.code === DONATION_ERROR_CODES.DONOR_ID_REQUIRED;

/**
 * What else a tax receipt needs that only Settings can add. Asked before the IC sheet, so a donor
 * is never asked for an IC and then told the receipt can't be made anyway.
 */
export const missingProfileMessage = (
  profile: DonorTaxProfile
): string | null => {
  if (!profile.name_on_file) return RECEIPT_DOWNLOAD_MESSAGES.nameRequired;
  if (!profile.email_on_file) return RECEIPT_DOWNLOAD_MESSAGES.emailRequired;
  return null;
};

/**
 * Downloads a donation's receipt, or one charity's receipts for a year. A tax receipt prints the
 * donor's ID number, so a donor whose nationality needs one and who has none on file is asked
 * for it first, and the download follows the save. The backend has the last word: a download it
 * refuses for want of the ID opens the same sheet. Every other download goes straight ahead.
 *
 * Render `<AddIcNumberSheet {...sheet} />` next to the buttons that call these.
 */
export const useDonationReceiptDownload = (enabled = true) => {
  const taxProfile = useDonorTaxProfile(enabled);
  const [awaitingId, setAwaitingId] = useState<PendingDownload | null>(null);

  /** Asks for the ID, unless Settings must add the name or email first. */
  const askForId = (pending: PendingDownload, profile: DonorTaxProfile) => {
    const missing = missingProfileMessage(profile);
    if (missing) toast.error(missing);
    else setAwaitingId(pending);
  };

  const run = (download: () => Promise<void>, kind: ReceiptDownloadKind) =>
    download().catch(async (error: unknown) => {
      if (isIdRequired(error)) {
        const { data: profile } = await taxProfile.refetch();
        if (profile?.id_rule) {
          askForId({ download, kind }, profile);
          return;
        }
      }
      toast.error(receiptDownloadMessage(error, kind));
    });

  const start = (
    download: () => Promise<void>,
    kind: ReceiptDownloadKind,
    mayNeedId: boolean
  ) => {
    const profile = taxProfile.data;
    // The profile already says the ID is missing for a receipt that needs it.
    if (mayNeedId && profile?.id_rule && !profile.has_ic_number)
      askForId({ download, kind }, profile);
    else run(download, kind);
  };

  /** One donation's receipt; `deductibleForDonor` from the donation. */
  const download = (donationRef: string, deductibleForDonor: boolean) =>
    start(
      () => downloadDonationReceipt(donationRef),
      'receipt',
      deductibleForDonor
    );

  /** All of one charity's receipts in a year; `taxDeductible` is the charity's status. */
  const downloadAll = (
    charityId: string,
    year: number,
    taxDeductible: boolean
  ) =>
    start(
      () => downloadCharityReceipts(charityId, year),
      'bundle',
      taxDeductible
    );

  const sheet = {
    open: awaitingId !== null,
    purpose: 'receipt' as AddIcNumberPurpose,
    onOpenChange: (open: boolean) => {
      if (!open) setAwaitingId(null);
    },
    onSaved: () => {
      if (awaitingId) run(awaitingId.download, awaitingId.kind);
    }
  };

  return { download, downloadAll, sheet };
};
